import { usePanelStore, useWorkplaceStore } from '@host/stores';
import { useMultiFloorStateStore } from '@/apps/office/stores/MultiFloorStore';
import { useMyFloorStore } from '@/apps/office/stores/myFloorStore';
import { isPostHogEnabled } from '@host/config/posthog';
import { trackEvent } from '@host/utils/posthog-track';

export type ChatType = 'rocketchat' | 'classic' | 'webex' | 'chatbot';

export type ChatOpenSource =
  | 'toolbar'
  | 'guest_toolbar'
  | 'guest_auto_open'
  | 'meeting_menu'
  | 'additional_apps'
  | 'people_search'
  | 'floor_avatar'
  | 'cx_floor_avatar'
  | 'auditorium_participant'
  | 'auditorium_join'
  | 'breakout_moderator'
  | 'platform_admin'
  | 'in_app_notification'
  | 'push_notification';

// Where each chat is shown; a surface is open or closed independently of the others.
type ChatSurface = 'drawer' | 'panel' | 'chatbot';

// An entry point calls setChatOpenSource() just before opening a chat, and the watchers below
// attach it to chat_opened once the chat actually becomes visible. It expires so a source whose
// open never happened (e.g. the chat was already open) isn't credited to a later, unrelated open.
const SOURCE_TTL_MS = 5000;
let pendingSource: { source: ChatOpenSource; at: number } | null = null;

const openChats = new Map<ChatSurface, { chatType: ChatType; openedAt: number }>();

export const setChatOpenSource = (source: ChatOpenSource) => {
  pendingSource = { source, at: Date.now() };
};

const takeChatOpenSource = (): ChatOpenSource | 'other' => {
  const pending = pendingSource;
  pendingSource = null;
  return pending && Date.now() - pending.at < SOURCE_TTL_MS ? pending.source : 'other';
};

const closeChat = (surface: ChatSurface, closeReason: 'closed' | 'page_unload') => {
  const current = openChats.get(surface);
  if (!current) return;
  openChats.delete(surface);
  trackEvent(
    'chat_closed',
    {
      chat_type: current.chatType,
      duration_seconds: Math.round((Date.now() - current.openedAt) / 1000),
      close_reason: closeReason,
    },
    // A normal request is dropped when the page is going away; a beacon still gets through.
    closeReason === 'page_unload' ? { transport: 'sendBeacon' } : undefined
  );
};

/** Emits chat_opened / chat_closed on visibility transitions only; repeated calls are no-ops. */
export const setChatVisible = (surface: ChatSurface, chatType: ChatType, visible: boolean) => {
  if (!visible) {
    closeChat(surface, 'closed');
    return;
  }
  if (openChats.has(surface)) return;
  openChats.set(surface, { chatType, openedAt: Date.now() });
  trackEvent('chat_opened', { chat_type: chatType, source: takeChatOpenSource() });
};

const isCurrentFloorWebex = () => {
  const floorId = useMyFloorStore.getState().currentFloorId || '';
  return useMultiFloorStateStore.getState().getFloorState(floorId)?.place?.type === 'webex';
};

/**
 * Watches the stores that decide chat visibility, so every open/close path is covered —
 * the Rocket.Chat drawer is closed from a dozen places, not just the toolbar icon.
 * The AI Assistant view reports its own visibility (see AiAssistantView).
 */
export const registerChatTracking = () => {
  if (!isPostHogEnabled) return;

  useWorkplaceStore.subscribe((state) =>
    setChatVisible(
      'drawer',
      'rocketchat',
      state.drawerOpen && state.drawerView?.appId === 'rocketchat'
    )
  );

  usePanelStore.subscribe((state) =>
    setChatVisible(
      'panel',
      isCurrentFloorWebex() ? 'webex' : 'classic',
      state.panels.some((panel) => panel.appId === 'chat' && !panel.isHidden)
    )
  );

  window.addEventListener('pagehide', () => {
    for (const surface of [...openChats.keys()]) closeChat(surface, 'page_unload');
  });
};
